import React, { useState, useRef, useCallback } from 'react';

const TASK_OPTIONS = [
  { label: 'A', name: 'Seizure Detection', desc: 'Detect seizure vs normal' },
  { label: 'B', name: 'Onset Localization', desc: '5-region onset zone' },
  { label: 'C', name: 'IED/Sharp Wave', desc: 'Interictal epileptiform discharge', beta: true },
];

export default function UploadPanel({ onAnalyze, isLoading }) {
  const [file, setFile] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState(['A']);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef();

  const handleFile = useCallback((f) => {
    if (f && (f.name.toLowerCase().endsWith('.edf') || f.name.toLowerCase().endsWith('.edf+'))) {
      setFile(f);
    } else if (f) {
      alert('Please select an EDF/EDF+ file');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  const toggleTask = (label) => {
    setSelectedTasks((prev) =>
      prev.includes(label) ? prev.filter((t) => t !== label) : [...prev, label]
    );
  };

  const handleSubmit = () => {
    if (file && selectedTasks.length > 0) {
      onAnalyze(file, selectedTasks);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <span className="icon">📂</span>
        <h2>Upload EEG Recording</h2>
      </div>

      {/* Drop Zone */}
      <div
        className={`upload-zone ${dragging ? 'dragging' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".edf,.edf+"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {file ? (
          <>
            <div className="icon-large">✅</div>
            <p style={{ fontWeight: 600 }}>{file.name}</p>
            <p className="format-note">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
          </>
        ) : (
          <>
            <div className="icon-large">🧠</div>
            <p>Drop EDF/EDF+ file here or click to browse</p>
            <p className="format-note">Supports EDF and EDF+ format, up to 500 MB</p>
          </>
        )}
      </div>

      {/* Task Selector */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 10 }}>
          Select analysis tasks:
        </div>
        <div className="task-selector">
          {TASK_OPTIONS.map(({ label, name, desc, beta }) => (
            <button
              key={label}
              className={`task-chip ${selectedTasks.includes(label) ? 'active' : ''}`}
              onClick={() => toggleTask(label)}
            >
              <span className="task-label">{label}</span>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {name}
                  {beta && (
                    <span style={{
                      marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                      background: 'var(--accent-yellow)', color: '#000',
                      fontSize: '0.65rem', fontWeight: 700, verticalAlign: 'middle'
                    }}>BETA</span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Analyze Button */}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button
          className="btn btn-primary"
          disabled={!file || selectedTasks.length === 0 || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? 'Analyzing...' : 'Analyze EEG'}
        </button>
      </div>
    </div>
  );
}
